// src/Pages/FullCatalog.jsx
// FullCatalog displays a searchable book grid with autocomplete suggestions.
// Fixes warnings: memoizes book list, updates effect dependencies, and adds aria-selected.
import parse from "html-react-parser";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CoIBooks, //KotRBooks,
   PPBooks, ContemporaryRomance } from "../Components/Books";
import "../CSS_stuff/full_catalog.css";

export default function FullCatalog() {

  const navigate = useNavigate();

  // Combine all series into one master list, memoized so it's stable
  const allBooks = useMemo(
    () => [ //...KotRBooks,
       ...CoIBooks, ...PPBooks, ...ContemporaryRomance],
    []
  );

  // The text in the search box
  const [searchQuery, setSearchQuery] = useState("");

  // Top matching books for autocomplete
  const [suggestions, setSuggestions] = useState([]);

  // Index of highlighted suggestion (-1 = none)
  const [activeIndex, setActiveIndex] = useState(-1);

  // Whether to show the suggestion dropdown
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Ref to wrapper div so clicks outside can close dropdown
  const wrapperRef = useRef(null);

  // Update suggestions whenever searchQuery or allBooks changes
  useEffect(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const matches = allBooks
        .filter(book => book.title.toLowerCase().includes(q))
        .slice(0, 5);
      setSuggestions(matches);
      setShowSuggestions(true);
      setActiveIndex(-1);
    } else {
      // empty query → hide dropdown
      setSuggestions([]);
      setShowSuggestions(false);
      setActiveIndex(-1);
    }
  }, [searchQuery, allBooks]); // include allBooks per ESLint recommendation

  // Close suggestions if click happens outside the autocomplete wrapper
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Keyboard navigation: up/down to move, Enter to select
  const handleKeyDown = e => {
    if (!showSuggestions) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    }
  };

  // Navigate to /books/:link with the selected book data
  const handleSelect = book => {
    setSearchQuery(book.title);
    setShowSuggestions(false);
    navigate(`/books/${book.link}`, { state: { book } });
  };

  // Fallback grid filtering when no dropdown is shown
  const filteredBooks = allBooks.filter(book =>
    book.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="catalog-container">
      <h1>Full Book Catalog</h1>

      {/*This next part add a button that will go to the suggested reading order on the lore page*/}
      <button
        type="button"
        className="read-order-btn"
        onClick={() => { window.location.href = '/lore#read-order'; }}
      >
        Click here for Suggested Reading Order
      </button>

      {/* Autocomplete input and suggestions */}
      <div className="autocomplete-wrapper" ref={wrapperRef}>
        <input
          type="text"
          className="catalog-search"
          placeholder="Search books..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => searchQuery && setShowSuggestions(true)}
        />

        {showSuggestions && suggestions.length > 0 && (
          <ul className="autocomplete-list" role="listbox">
            {suggestions.map((book, idx) => (
              <li
                key={book.link}
                role="option"
                aria-selected={idx === activeIndex}
                className={idx === activeIndex ? "active" : ""}
                onMouseDown={() => handleSelect(book)}
              >
                {parse(book.title)}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Grid view of all matching books */}
      <div className="catalog-grid">
        {filteredBooks.map(book => (
          <div
            key={book.link}
            className="catalog-item"
            onClick={() => navigate(`/books/${book.link}`, { state: { book } })}
          >
            <img
              src={book.image}
              alt={`${book.title} cover`}
              className="catalog-cover"
              loading="lazy"
            />
            <p>{parse(book.title)}</p>
          </div>
        ))}
      </div>

      {/* Message when no matches found */}
      {filteredBooks.length === 0 && (
        <p className="no-results">No books match your search.</p>
      )}
    </div>
  );
}